import { ContextType, useContext, useEffect, useMemo } from "react";

import { Stripe, StripeCardNumberElement } from "@stripe/stripe-js";

import { DateTime } from "luxon";

import AddToCartMutation from "api/mutations/AddToCartMutation";
import BeginOrderPlacementMutation from "api/mutations/BeginOrderPlacementMutation";
import ClearCartMutation from "api/mutations/ClearCartMutation";
import CompleteOrderPlacementMutation from "api/mutations/CompleteOrderPlacementMutation";
import RemoveFromCartMutation from "api/mutations/RemoveFromCartMutation";
import UpdateOrderCustomerMutation from "api/mutations/UpdateOrderCustomerMutation";
import BusinessOrderType from "api/types/BusinessOrderType";
import CartContext, { CustomerInformation } from "contexts/CartContext";

import calculateCart from "helpers/calculateCart";
import StateOf from "utility/StateOf";
import bindMethods from "utility/bindMethods";

import useBusiness from "./useBusiness";
import useMutation from "./useMutation";
import useTracking from "./useTracking";

type Context = ContextType<typeof CartContext>;
type State = StateOf<Context>;
type CartItem = State["cartItems"][number];

const getTipCents = (state: State): number => {
	const defaultTipPercentage = 15;
	return state.tipCents != null
		? state.tipCents
		: Math.round(((state.tipPercentage || defaultTipPercentage) / 100) * state.cartTotalCents);
};

const validate = (state: State): State => state; // TODO

const calculate = (state: State, taxRate: number): State => ({
	...state,
	...calculateCart(
		state,
		taxRate,
		getTipCents(state),
		state?.orderType?.priceCents || undefined,
		state?.orderType?.isDeliveryTaxable || undefined,
		state?.percentOff || undefined
	)
});

const calculateAndValidate = (state: State, taxRate: number): State => validate(calculate(state, taxRate));

const getMinPreparationTime = (
	menuPreparationTimeMinutes,
	orderTypePreparationTypeMinutes,
	contextPreparationTimeMinutes
) => {
	let minV = orderTypePreparationTypeMinutes;

	minV = menuPreparationTimeMinutes && menuPreparationTimeMinutes > minV ? menuPreparationTimeMinutes : minV;

	minV = contextPreparationTimeMinutes && contextPreparationTimeMinutes > minV ? contextPreparationTimeMinutes : minV;

	return minV;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useCart = () => {
	const { business } = useBusiness() || {};
	const [addToCart] = useMutation(AddToCartMutation);
	const [removeFromCart] = useMutation(RemoveFromCartMutation);
	const [clearCart] = useMutation(ClearCartMutation);
	const [updateOrderCustomer] = useMutation(UpdateOrderCustomerMutation);
	const [beginOrderPlacement] = useMutation(BeginOrderPlacementMutation);
	const [completeOrderPlacement] = useMutation(CompleteOrderPlacementMutation);
	const context = useContext(CartContext);
	const { trackEvent } = useTracking();
	const { taxRate } = business;

	const updateState = useMemo(
		() => (changes: (state: State) => Partial<State>) =>
			context.setState(state =>
				calculateAndValidate(
					{
						...state,
						...changes(state)
					},
					taxRate!
				)
			),
		[context, taxRate]
	);

	const orderType = context.orderType || business.orderTypes[0];
	if (!context.orderType)
		updateState(() => ({
			orderType
		}));

	// sync context changes into local storage
	useEffect(() => {
		localStorage.setItem("cart", JSON.stringify(context));
	}, [context]);

	return useMemo(
		() =>
			bindMethods({
				...context,
				orderType: orderType,

				async addToCart(item: CartItem) {
					const {
						menuItem: { preparationTimeMinutes }
					} = item;
					const defaultPreparationTimeMinutes = orderType?.defaultPreparationTimeMinutes;

					updateState(state => ({
						cartItems: [...state.cartItems, item],
						cartItemsCount: [...state.cartItems, item].reduce((sum, i) => sum + i.quantity, 0),
						preparationTimeMinutes: getMinPreparationTime(
							preparationTimeMinutes,
							defaultPreparationTimeMinutes,
							context.preparationTimeMinutes
						)
					}));

					try {
						const { data } = await addToCart({
							variables: {
								addToCartInput: {
									businessId: business.id,
									// menuId: menu.id,
									// menuGroupId: group.id,
									menuId: item.menuItem.menuGroup!.menu!.id,
									menuGroupId: item.menuItem.menuGroup!.id,
									menuItemId: item.menuItem.id,
									orderId: context.orderId,
									item: {
										localId: item.localId,
										specialInstructions: item.specialInstructions,
										selectedOptions: item.selectedOptions.map(x => ({
											menuItemOptionGroupId: x.menuItemOptionGroupId,
											menuItemOptionId: x.menuItemOptionId
										})),
										quantity: item.quantity,
										unitPriceCents: item.unitPriceCents,
										extendedPriceCents: item.extendedPriceCents
									}
								}
							}
						});

						// console.log("addToCart() mutation succeeded");
						trackEvent({
							event: "add_to_cart",
							ecommerce: {
								items: [
									{
										item_name: item.menuItem.name,
										item_id: item.menuItem.id,
										quantity: item.quantity,
										price: item.unitPriceCents / 100,
										extendedPriceCents: item.extendedPriceCents
									}
								]
							}
						});

						updateState(() => ({ orderId: data?.addToCart?.order?.id || undefined }));
					} catch (exc) {
						console.error("addToCart() mutation failed:\r\n", exc);
					}
				},

				updateCartItem(item: CartItem) {
					updateState(state => {
						const updatedItems = state.cartItems.map(cartItem =>
							cartItem.localId === item.localId ? { ...item } : cartItem
						);
						return {
							cartItems: updatedItems,
							cartItemsCount: updatedItems.reduce((tot, i) => tot + i.quantity, 0)
						};
					});
				},

				async removeFromCart(item: CartItem) {
					updateState(state => ({
						cartItems: state.cartItems.filter(x => x.localId !== item.localId),
						cartItemsCount: state.cartItems
							.filter(x => x.localId !== item.localId)
							.reduce((tot, i) => tot + i.quantity, 0)
					}));

					try {
						const { data } = await removeFromCart({
							variables: {
								removeFromCartInput: {
									businessId: business.id,
									orderId: context.orderId,
									item: {
										localId: item.localId
									}
								}
							}
						});

						// console.log("removeFromCart() mutation succeeded");
						trackEvent({
							event: "remove_from_cart",
							ecommerce: {
								items: [
									{
										item_name: item.menuItem.name,
										item_id: item.menuItem.id,
										quantity: item.quantity,
										price: item.unitPriceCents / 100
									}
								]
							}
						});

						updateState(() => ({ orderId: data?.removeFromCart?.order?.id || undefined }));
					} catch (exc) {
						console.error("removeFromCart() mutation failed:\r\n", exc);
					}
				},

				async clearCart() {
					updateState(() => ({ cartItems: [], cartItemsCount: 0, percentOff: 0, promoCode: null }));

					try {
						const { data } = await clearCart({
							variables: {
								clearCartInput: {
									businessId: business.id,
									orderId: context.orderId
								}
							}
						});

						// console.log("clearCart() mutation succeeded");

						updateState(() => ({ orderId: data?.clearCart?.order?.id || undefined }));
					} catch (exc) {
						console.error("clearCart() mutation failed:\r\n", exc);
					}
				},

				setTipCents(tipCents: number | null, tipPercentage: number | null) {
					updateState(() => ({ tipCents, tipPercentage }));
				},

				getTipCents() {
					return getTipCents(context);
				},

				setPercentOff(percentOff: number | null) {
					updateState(() => ({ percentOff }));
				},

				setPromoCode(promoCode: string | null) {
					updateState(() => ({ promoCode }));
				},

				setPaymentInfo(paymentInfo: { cardNumber: string }) {
					updateState(() => ({ paymentInfo }));
				},

				setDeliveryOrPickupAt(deliveryOrPickupAt?: DateTime) {
					updateState(() => ({ deliveryOrPickupAt }));
				},
				setOrderType(orderType: BusinessOrderType) {
					updateState(() => ({ orderType }));
				},

				async saveCustomerInfo({ fullName, deliveryAddress, phoneNumber, email }: CustomerInformation) {
					updateState(state => ({ fullName, deliveryAddress, phoneNumber, email }));

					try {
						const { data } = await updateOrderCustomer({
							variables: {
								updateOrderCustomerInput: {
									businessId: business.id,
									orderId: context.orderId,
									fullName: fullName!,
									phoneNumber: phoneNumber!,
									email: email!
								}
							}
						});

						// console.log("updateOrderCustomer() mutation succeeded");

						updateState(() => ({ orderId: data?.updateOrderCustomer?.order?.id || undefined }));
					} catch (exc) {
						console.error("updateOrderCustomer() mutation failed:\r\n", exc);
					}
				},

				setCustomerInfo({ fullName, deliveryAddress, phoneNumber, email }: CustomerInformation) {
					updateState(state => ({ fullName, deliveryAddress, phoneNumber, email }));
				},

				getCustomerInfo(): CustomerInformation {
					return {
						fullName: context.fullName,
						deliveryAddress: context.deliveryAddress,
						phoneNumber: context.phoneNumber,
						email: context.email
					};
				},

				async placeOrder({ stripe, element }: { stripe: Stripe; element: StripeCardNumberElement }) {
					try {
						const deliveryOrPickupAt = context.getEffectiveDeliveryOrPickupAt();

						const { errors, data } = await beginOrderPlacement({
							variables: {
								beginOrderPlacementInput: {
									businessId: business.id,
									orderType: {
										orderTypeId: orderType!.id!,
										orderType: orderType!.orderType!
									},
									preparationTimeMinutes: context.preparationTimeMinutes,
									deliveryOrPickupAt,
									orderId: context.orderId,
									orderItems: context.cartItems.map(item => ({
										localId: item.localId,
										menuItem: {
											id: item.menuItem.id,
											priceCents: item.menuItem.priceCents,
											displayPrice: item.menuItem.displayPrice,
											preparationTimeMinutes: item.menuItem.preparationTimeMinutes
										},
										specialInstructions: item.specialInstructions,
										quantity: item.quantity,
										selectedOptions: item.selectedOptions.map(option => ({
											id: option.menuItemOptionId,
											priceCents: option.priceCents,
											displayPrice: option.displayPrice,
											preparationTimeMinutes: option.preparationTimeMinutes
										})),
										unitPriceCents: item.unitPriceCents,
										extendedPriceCents: item.extendedPriceCents
									})),
									orderSubTotalCents: context.cartTotalCents!,
									taxCents: context.taxCents!,
									tipCents: getTipCents(context),
									// del: context.tipCents,
									orderTotalCents: context.grandTotalCents!,
									promoCode: context.promoCode,
									discount: context.discount,
									customer: {
										fullName: context.fullName!,
										phoneNumber: context.phoneNumber!,
										email: context.email!
									},
									deliveryAddress: context.deliveryAddress
								}
							}
						});

						if (errors || !data) {
							errors?.forEach(error => {
								console.error("order placement failed:\r\n", error);
							});

							throw new Error("Order placement failed");
						}

						const {
							paymentIntentKey,
							order: { id: orderId }
						} = data.beginOrderPlacement;

						const { error } = await stripe.confirmCardPayment(paymentIntentKey, {
							payment_method: {
								card: element
							}
						});

						if (error) {
							console.error("order confirmation failed:\r\n", error);
							throw error;
						}

						// console.log("order confirmed:", JSON.stringify(paymentIntent, null, 2));

						await completeOrderPlacement({
							variables: {
								completeOrderPlacement: {
									businessId: business.id,
									orderId: orderId
								}
							}
						});

						updateState(() => ({
							cartItems: [],
							cartItemsCount: 0,
							percentOff: 0,
							promoCode: null,
							discount: 0,
							isConfirmationVisible: true
						}));
					} catch (exc) {
						console.error("order placement failed:\r\n", exc);
						throw exc;
					}
				},

				showOrderConfirmation() {
					updateState(state => ({ ...state, isConfirmationVisible: true }));
				},

				hideOrderConfirmation() {
					updateState(state => ({ ...state, isConfirmationVisible: false }));
				}
			}),
		[
			addToCart,
			business.id,
			clearCart,
			context,
			orderType,
			beginOrderPlacement,
			completeOrderPlacement,
			removeFromCart,
			trackEvent,
			updateOrderCustomer,
			updateState
		]
	);
};

export default useCart;
